import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../services/api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

/**
 * Session cache for lazy entity list/search (Add dialog List mode, enrichment picker).
 * Keyed by entity type + query options + search term.
 */
const ENTITY_SEARCH_CACHE = new Map();

const normalizeEntityType = (entityType) =>
  entityType
    ? String(entityType)
        .trim()
        .toLowerCase()
        .replace(/_/g, "-")
        .replace(/\s+/g, "-")
    : null;

const buildSearchCacheKey = ({
  entityType,
  pageSize,
  includeUnapproved,
  approvalStatus,
  architectureExplorer,
  searchTerm,
}) => {
  const type = normalizeEntityType(entityType) || "_any";
  const useArchitectureExplorer =
    typeof architectureExplorer === "boolean"
      ? architectureExplorer
      : type !== "solution-component";
  const q = String(searchTerm || "").trim().toLowerCase();
  return [
    type,
    `ps:${pageSize}`,
    `arch:${useArchitectureExplorer ? 1 : 0}`,
    `unapp:${includeUnapproved ? 1 : 0}`,
    `status:${String(approvalStatus || "")}`,
    `q:${q}`,
  ].join("|");
};

const readSearchCache = (key) => {
  if (!key || !ENTITY_SEARCH_CACHE.has(key)) return null;
  const cached = ENTITY_SEARCH_CACHE.get(key);
  if (!cached?.loaded) return null;
  return {
    results: Array.isArray(cached.results) ? cached.results.map((e) => ({ ...e })) : [],
    total: cached.total || 0,
    hasMore: Boolean(cached.hasMore),
    currentPage: cached.currentPage || 1,
    searchTerm: cached.searchTerm || "",
  };
};

const writeSearchCache = (key, entry) => {
  if (!key) return;
  ENTITY_SEARCH_CACHE.set(key, {
    loaded: true,
    results: Array.isArray(entry.results) ? entry.results.map((e) => ({ ...e })) : [],
    total: entry.total || 0,
    hasMore: Boolean(entry.hasMore),
    currentPage: entry.currentPage || 1,
    searchTerm: entry.searchTerm || "",
  });
};

/** Drop list/search session cache (e.g. after creating an entity in Add dialog). */
export const invalidateEntitySearchCache = (entityType = null) => {
  if (!entityType) {
    ENTITY_SEARCH_CACHE.clear();
    return;
  }
  const prefix = `${normalizeEntityType(entityType)}|`;
  for (const key of Array.from(ENTITY_SEARCH_CACHE.keys())) {
    if (key.startsWith(prefix)) ENTITY_SEARCH_CACHE.delete(key);
  }
};

/**
 * Hook for lazy-loading entity search with debouncing
 * Used by Add dialogs to search entities on-demand instead of pre-fetching all
 */
export const useEntitySearch = (entityType, options = {}) => {
  const {
    debounceMs = 300,
    pageSize = 50,
    enabled = true,
    includeUnapproved = false,
    approvalStatus = "",
    architectureExplorer,
  } = options;

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Normalize entity type (handle both formats)
  const normalizedType = normalizeEntityType(entityType);

  const cacheOptions = {
    entityType: normalizedType,
    pageSize,
    includeUnapproved,
    approvalStatus,
    architectureExplorer,
  };

  const applyCachedEntry = useCallback((cached, searchTerm) => {
    setResults(cached.results);
    setTotal(cached.total);
    setHasMore(cached.hasMore);
    setCurrentPage(cached.currentPage);
    setCurrentSearchTerm(
      typeof searchTerm === "string" ? searchTerm : cached.searchTerm
    );
    setError(null);
    setLoading(false);
  }, []);

  const searchEntities = useCallback(
    async (searchTerm = "", page = 1, reset = false) => {
      if (!enabled || !normalizedType) {
        return;
      }

      const cacheKey = buildSearchCacheKey({
        ...cacheOptions,
        searchTerm,
      });

      // Restore full session for this query on first-page / reset (reopen Add dialog).
      if (reset || page === 1) {
        const cached = readSearchCache(cacheKey);
        if (cached) {
          applyCachedEntry(cached, searchTerm);
          return;
        }
      } else {
        // Load-more: skip network if this page was already cached in the session.
        const cached = readSearchCache(cacheKey);
        if (cached && page <= cached.currentPage) {
          applyCachedEntry(cached, searchTerm);
          return;
        }
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        // Use entities-paginated endpoint which supports entity_type filtering
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });

        // Add search term if provided
        if (searchTerm) {
          params.append("search", searchTerm);
        }

        // Add entity type filter if provided
        if (normalizedType) {
          params.append("entity_type", normalizedType);
        }

        // Don't include relationships for faster response
        params.append("include_relationships", "false");
        const useArchitectureExplorer =
          typeof architectureExplorer === "boolean"
            ? architectureExplorer
            : normalizedType !== "solution-component";
        if (useArchitectureExplorer) {
          params.append("architecture_explorer", "true");
        }
        if (includeUnapproved) {
          params.append("include_unapproved", "true");
        }
        if (approvalStatus) {
          params.append("approval_status", approvalStatus);
        }

        const url = `/api/entities-paginated?${params.toString()}`;
        const response = await apiFetch(url, {
          signal: abortControllerRef.current.signal,
          timeout: 30000, // 30 seconds timeout for entity search
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        const entities = data?.data?.entities || data?.entities || [];
        const pagination = data?.data?.pagination || data?.pagination || {};
        const totalCount = pagination.total_items || pagination.total || 0;
        const nextHasMore =
          entities.length === pageSize && page * pageSize < totalCount;

        const prior =
          reset || page === 1
            ? []
            : readSearchCache(cacheKey)?.results || [];
        const mergedResults =
          reset || page === 1 ? entities : [...prior, ...entities];

        setResults(mergedResults);
        setTotal(totalCount);
        setHasMore(nextHasMore);
        setCurrentPage(page);
        setCurrentSearchTerm(searchTerm);

        writeSearchCache(cacheKey, {
          results: mergedResults,
          total: totalCount,
          hasMore: nextHasMore,
          currentPage: page,
          searchTerm,
        });
      } catch (err) {
        if (err.name === "AbortError") {
          // Request was cancelled, ignore
          return;
        }
        console.error("Entity search error:", err);
        setError(err.message || "Failed to search entities");
        if (reset || page === 1) {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      normalizedType,
      pageSize,
      enabled,
      includeUnapproved,
      approvalStatus,
      architectureExplorer,
      applyCachedEntry,
      // cacheOptions fields covered above
    ]
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchTerm = "", reset = true) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        searchEntities(searchTerm, 1, reset);
      }, debounceMs);
    },
    [searchEntities, debounceMs]
  );

  // Load more results
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      searchEntities(currentSearchTerm, currentPage + 1, false);
    }
  }, [loading, hasMore, currentPage, currentSearchTerm, searchEntities]);

  // Initial load when dialog opens (empty search to get first page)
  const initialLoad = useCallback(() => {
    searchEntities("", 1, true);
  }, [searchEntities]);

  // Reset search state (stable callback to avoid effect loops in consumers)
  // Does not clear the session cache — reopen rehydrates via initialLoad.
  const reset = useCallback(() => {
    setResults((prev) => (prev.length ? [] : prev));
    setCurrentPage(1);
    setHasMore(false);
    setTotal(0);
    setCurrentSearchTerm("");
    setError(null);
    setLoading(false);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    results,
    loading,
    error,
    hasMore,
    total,
    search: debouncedSearch,
    loadMore,
    initialLoad,
    reset,
  };
};
